// import axios from "axios"
import { reactive, ref } from 'vue';
import { defineStore } from 'pinia';
import { useAuthStore } from '@/stores/auth_store';
import { useErrorStore } from "@/stores/error_store";
import { axiosQuery } from "@/assets/js/utils";
import { BUILDING_USE_TYPES_RESIDENTIAL_ARRAY, BUILDING_USE_TYPES_COMMERCIAL_ARRAY, BUILDING_USE_TYPES_INDUSTRIAL_ARRAY, BUILDING_USE_TYPES_HOSPITALITY_ARRAY, BUILDING_USE_TYPES_RETAIL_ARRAY, BUILDING_USE_TYPES_CIVIC_AND_CULTURAL_ARRAY } from '@/types/Constants'
const apiBase = process.env.VUE_APP_ENV_API_BASE || "http://167.172.25.255:3000/api/v1/"

let authTokenHeader = reactive({});
let authToken = ref('');

export const useDropdownStore = defineStore('DropdownStore', {
    id: 'dropdownStore',
    state: () => ({
        assetCategoryTypes: [],
        constructionTypes: [],
        finishLevelTypes: [],
        parkingTypes: [],
        buildingUseTypes: [],
        contractTypes: [],
        buildingUseTypeForResidential: [],
        buildingUseTypeForRetail: [],
        formerAssetCategories: [],
        buildingUseTypeForCommercial: [],
        buildingUseTypeForIndustrial: [],
        buildingUseTypeForHospitality: [],
        buildingUseTypeForCivicAndCultural: [],
        sustainabilityTypes: [],
        amenityTypes: [],
        brandTypes: [],
        consultantTypes: [],
        investorSponsors: [],
        generalContractors: [],
        consultants: [],
        assetCategoryFilteredTypes: [],
        buildingUseFilteredTypes: [],
    }),
    actions: {
        getAssetCategoryTypes(filterValues) {
            const authStore = useAuthStore();
            const errorStore = useErrorStore();
            authToken.value = authStore.getAuthToken;
            authTokenHeader.value = authStore.getAuthTokenHeader;

            if (!authToken.value) {
                return Promise.reject(new Error("Auth token is missing"));
            }

            try {
                axiosQuery(
                    `${apiBase}codes?type=FormerAssetCategory`,
                    authTokenHeader.value,
                    (data) => {
                        // Map FormerAssetCategory IDs (237-242) to AssetCategory IDs (184-189) for backend compatibility
                        const idMapping = {
                            237: 184, // Residential
                            238: 185, // Commercial
                            239: 186, // Industrial  
                            240: 187, // Hospitality
                            241: 188, // Retail
                            242: 189  // Civic & Cultural
                        };
                        
                        this.assetCategoryTypes = data.map(item => ({ 
                            ...item, 
                            frontendId: item.id,  // Keep original for display
                            id: idMapping[item.id] || item.id,  // Use mapped ID for API calls
                            isChecked: false 
                        }));
                        
                        // Filter asset category types based on the provided filter values
                        if (filterValues) {
                          this.assetCategoryFilteredTypes = this.assetCategoryTypes.filter(type => filterValues.includes(type.code));
                        }
                    }
                );
            } catch (error) {
                errorStore.setError('Dropdown', 'An error occurred in retrieving Asset Category Types.', 'error', false);
                return Promise.reject(error);
            }

        },
        getConstructionTypes() {
            const authStore = useAuthStore();
            const errorStore = useErrorStore();
            authToken.value = authStore.getAuthToken;
            authTokenHeader.value = authStore.getAuthTokenHeader;
            if (!authToken.value) {
                return Promise.reject(new Error("Auth token is missing"));
            }

            try {
                axiosQuery(
                    `${apiBase}codes?type=ConstructionType`,
                    authTokenHeader.value,
                    (data) => {
                        this.constructionTypes = data.map(item => ({ ...item, isChecked: false }));
                    }
                );
            } catch (error) {
                errorStore.setError('Dropdown', 'An error occurred in retrieving Construction Types.', 'error', false);
                return Promise.reject(error)
            }
        },
        getInteriorFinishLevelTypes() {
            const authStore = useAuthStore();
            const errorStore = useErrorStore();
            authToken.value = authStore.getAuthToken;
            authTokenHeader.value = authStore.getAuthTokenHeader;

            if (!authToken.value) {
                return Promise.reject(new Error("Auth token is missing"));
            }

            try {
                axiosQuery(
                    `${apiBase}codes?type=FinishLevel`,
                    authTokenHeader.value,
                    (data) => {
                        this.finishLevelTypes = data;
                    },
                );
            } catch (error) {
                errorStore.setError('Dropdown', 'An error occurred in retrieving Finish Level Types.', 'error', false);
                return Promise.reject(error);
            }
        },
        getParkingTypes() {
            const authStore = useAuthStore();
            const errorStore = useErrorStore();
            authToken.value = authStore.getAuthToken;
            authTokenHeader.value = authStore.getAuthTokenHeader;

            if (!authToken.value) {
                return Promise.reject(new Error("Auth token is missing"));
            }

            try {
                axiosQuery(
                    `${apiBase}codes?type=ParkingType`,
                    authTokenHeader.value,
                    (data) => {
                        this.parkingTypes = data.map(item => ({ ...item, isChecked: false }));
                    }
                );
            } catch (error) {
                errorStore.setError('Dropdown', 'An error occurred in retrieving Parking Types.', 'error', false);
                return Promise.reject(error)
            }
        },
        getBuildingUseTypes(filterValues) {
            const authStore = useAuthStore();
            const errorStore = useErrorStore();
            authToken.value = authStore.getAuthToken;
            authTokenHeader.value = authStore.getAuthTokenHeader;

            if (!authToken.value) {
                return Promise.reject(new Error("Auth token is missing"));
            }

            try {
                axiosQuery(
                    `${apiBase}codes?type=BuildingUseType`,
                    authTokenHeader.value,
                    (data) => {
                        this.buildingUseTypes = data.map(item => ({ ...item, isChecked: false }));

                        const forResidentialType = BUILDING_USE_TYPES_RESIDENTIAL_ARRAY;
                        const forRetailType = BUILDING_USE_TYPES_RETAIL_ARRAY;
                        const forCommercialType = BUILDING_USE_TYPES_COMMERCIAL_ARRAY;
                        const forIndustrialType = BUILDING_USE_TYPES_INDUSTRIAL_ARRAY;
                        const forHospitalityType = BUILDING_USE_TYPES_HOSPITALITY_ARRAY;
                        const forCivicAndCulturalType = BUILDING_USE_TYPES_CIVIC_AND_CULTURAL_ARRAY;

                        this.buildingUseTypeForResidential = this.buildingUseTypes.filter(item => forResidentialType.includes(item.code));
                        this.buildingUseTypeForRetail = this.buildingUseTypes.filter(item => forRetailType.includes(item.code));
                        this.buildingUseTypeForCommercial = this.buildingUseTypes.filter(item => forCommercialType.includes(item.code));
                        this.buildingUseTypeForIndustrial = this.buildingUseTypes.filter(item => forIndustrialType.includes(item.code));
                        this.buildingUseTypeForHospitality = this.buildingUseTypes.filter(item => forHospitalityType.includes(item.code));
                        this.buildingUseTypeForCivicAndCultural = this.buildingUseTypes.filter(item => forCivicAndCulturalType.includes(item.code));

                        // Filter building use types based on the provided filter values
                        if (filterValues) {
                          this.buildingUseFilteredTypes = this.buildingUseTypes.filter(type => filterValues.includes(type.code));
                        }
                    }
                );
            } catch (error) {
                errorStore.setError('Dropdown', 'An error occurred in retrieving Building Use Types.', 'error', false);
                return Promise.reject(error);
            }
        },
        getContractTypes() {
            const authStore = useAuthStore();
            const errorStore = useErrorStore();
            authToken.value = authStore.getAuthToken;
            authTokenHeader.value = authStore.getAuthTokenHeader;

            if (!authToken.value) {
                return Promise.reject(new Error("Auth token is missing"));
            }

            try {
                axiosQuery(
                    `${apiBase}codes?type=ContractType`,
                    authTokenHeader.value,
                    (data) => {
                        this.contractTypes = Array.isArray(data) ? data.sort((a, b) => {
                            return (a && a.code && b && b.code) ? a.code.localeCompare(b.code) : 0;
                        }) : [];
                    },

                );
            } catch (error) {
                errorStore.setError('Dropdown', 'An error occurred in retrieving Contract Types.', 'error', false);
                return Promise.reject(error);
            }
        },
        getFormerAssetCategories() {
            const authStore = useAuthStore();
            const errorStore = useErrorStore();
            authToken.value = authStore.getAuthToken;
            authTokenHeader.value = authStore.getAuthTokenHeader;

            if (!authToken.value) {
                return Promise.reject(new Error("Auth token is missing"));
            }

            try {
                axiosQuery(
                    `${apiBase}codes?type=FormerAssetCategory`,
                    authTokenHeader.value,
                    (data) => {
                        this.formerAssetCategories = data;
                    }
                );
            } catch (error) {
                errorStore.setError('Dropdown', 'An error occurred in retrieving Former Asset Category Types.', 'error', false);
                return Promise.reject(error);
            }
        },
        getBrandTypes() {
            const authStore = useAuthStore();
            const errorStore = useErrorStore();
            authToken.value = authStore.getAuthToken;
            authTokenHeader.value = authStore.getAuthTokenHeader;

            if (!authToken.value) {
                return Promise.reject(new Error("Auth token is missing"));
            }

            try {
                axiosQuery(
                    `${apiBase}codes?type=BrandType`,
                    authTokenHeader.value,
                    (data) => {
                        this.brandTypes = Array.isArray(data) ? data.sort((a, b) => {
                            return (a && a.code && b && b.code) ? a.code.localeCompare(b.code) : 0;
                        }) : [];
                    }
                );
            } catch (error) {
                errorStore.setError('Dropdown', 'An error occurred in retrieving Brand Types.', 'error', false);
                return Promise.reject(error);
            }
        },
        getSustainabilityTypes() {
            const authStore = useAuthStore();
            const errorStore = useErrorStore();
            authToken.value = authStore.getAuthToken;
            authTokenHeader.value = authStore.getAuthTokenHeader;

            if (!authToken.value) {
                return Promise.reject(new Error("Auth token is missing"));
            }

            try {
                axiosQuery(
                    `${apiBase}codes?type=SustainabilityType`,
                    authTokenHeader.value,
                    (data) => {
                        this.sustainabilityTypes = Array.isArray(data) ? data.sort((a, b) => {
                            return (a && a.code && b && b.code) ? a.code.localeCompare(b.code) : 0;
                        }) : [];
                    }
                );
            } catch (error) {
                errorStore.setError('Dropdown', 'An error occurred in retrieving Sustainability Types.', 'error', false);
                return Promise.reject(error);
            }
        },
        getAmenityTypes() {
            const authStore = useAuthStore();
            const errorStore = useErrorStore();
            authToken.value = authStore.getAuthToken;
            authTokenHeader.value = authStore.getAuthTokenHeader;

            if (!authToken.value) {
                return Promise.reject(new Error("Auth token is missing"));
            }

            try {
                axiosQuery(
                    `${apiBase}codes?type=AmenityType`,
                    authTokenHeader.value,
                    (data) => {
                        this.amenityTypes = Array.isArray(data) ? data.sort((a, b) => {
                            return (a && a.code && b && b.code) ? a.code.localeCompare(b.code) : 0;
                        }) : [];
                    }
                );
            } catch (error) {
                errorStore.setError('Dropdown', 'An error occurred in retrieving Amenity Types.', 'error', false);
                return Promise.reject(error);
            }
        },
        getConsultantTypes() {
            const authStore = useAuthStore();
            const errorStore = useErrorStore();
            authToken.value = authStore.getAuthToken;
            authTokenHeader.value = authStore.getAuthTokenHeader;

            if (!authToken.value) {
                return Promise.reject(new Error("Auth token is missing"));
            }

            try {
                axiosQuery(
                    `${apiBase}codes?type=ConsultantType`,
                    authTokenHeader.value,
                    (data) => {
                        this.consultantTypes = Array.isArray(data) ? data.sort((a, b) => {
                            return (a && a.code && b && b.code) ? a.code.localeCompare(b.code) : 0;
                        }) : [];
                    }
                );
            } catch (error) {
                errorStore.setError('Dropdown', 'An error occurred in retrieving Consultant Types.', 'error', false);
                return Promise.reject(error);
            }
        },
        getInvestorSponsors() {
            const authStore = useAuthStore();
            const errorStore = useErrorStore();
            authToken.value = authStore.getAuthToken;
            authTokenHeader.value = authStore.getAuthTokenHeader;

            if (!authToken.value) {
                return Promise.reject(new Error("Auth token is missing"));
            }

            try {
                axiosQuery(
                    `${apiBase}investor_sponsors`,
                    authTokenHeader.value,
                    (data) => {
                        this.investorSponsors = Array.isArray(data) ? data.sort((a, b) => {
                            return (a && a.name && b && b.name) ? a.name.localeCompare(b.name) : 0;
                        }) : [];
                    }
                );
            } catch (error) {
                errorStore.setError('Dropdown', 'An error occurred in retrieving Investor Sponsors.', 'error', false);
                return Promise.reject(error);
            }
        },
        async getGeneralContractors() {
            const authStore = useAuthStore();
            const errorStore = useErrorStore();
            authToken.value = authStore.getAuthToken;
            authTokenHeader.value = authStore.getAuthTokenHeader;

            if (!authToken.value) {
                return Promise.reject(new Error("Auth token is missing"));
            }

            try {
                return new Promise((resolve, reject) => {
                    axiosQuery(
                        `${apiBase}contractors`,
                        authTokenHeader.value,
                        (data) => {
                            this.generalContractors = Array.isArray(data) ? data.sort((a, b) => {
                                return (a && a.name && b && b.name) ? a.name.localeCompare(b.name) : 0;
                            }) : [];
                            console.log('✅ Contractors loaded in store:', this.generalContractors.length);
                            resolve(this.generalContractors);
                        },
                        errorStore,
                        'Contractors'
                    ).catch(error => {
                        console.error('❌ Error loading contractors:', error);
                        reject(error);
                    });
                });
            } catch (error) {
                errorStore.setError('Dropdown', 'An error occurred in retrieving Contractors.', 'error', false);
                return Promise.reject(error);
            }
        },
        getConsultants() {
            const authStore = useAuthStore();
            const errorStore = useErrorStore();
            authToken.value = authStore.getAuthToken;
            authTokenHeader.value = authStore.getAuthTokenHeader;

            if (!authToken.value) {
                return Promise.reject(new Error("Auth token is missing"));
            }

            try {
                axiosQuery(
                    `${apiBase}consultants`,
                    authTokenHeader.value,
                    (data) => {
                        this.consultants = Array.isArray(data) ? data.sort((a, b) => {
                            return (a && a.name && b && b.name) ? a.name.localeCompare(b.name) : 0;
                        }) : [];
                    }
                );
            } catch (error) {
                errorStore.setError('Dropdown', 'An error occurred in retrieving Consultants.', 'error', false);
                return Promise.reject(error);
            }
        },
    },
});